import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const ProcessDocumentation = ({ processSteps }) => {
  const [activeStep, setActiveStep] = useState(0);

  return (
    <section className="py-16 bg-background">
      <div className="container mx-auto px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl lg:text-4xl font-black text-primary mb-4 color-brand-dark">
              Behind the Scenes
            </h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Transparent documentation of our education design process, learner challenges, and how we solved them.
            </p>
          </div>

          {/* Step Navigation */}
          <div className="flex flex-wrap justify-center gap-2 sm:gap-4 mb-8 sm:mb-12 px-2">
            {processSteps?.map((step, index) => (
              <button
                key={index}
                onClick={() => setActiveStep(index)}
                className={`px-3 sm:px-6 py-2 sm:py-3 font-bold border-2 transition-all duration-300 text-sm sm:text-base ${
                  activeStep === index
                    ? 'bg-accent text-accent-foreground border-accent brutalist-shadow'
                    : 'bg-card text-card-foreground border-concrete hover:bg-concrete-light'
                }`}
              >
                <span className="hidden lg:inline">{step?.title}</span>
                <span className="lg:hidden">Step {index + 1}</span>
              </button>
            ))}
          </div>

          {/* Active Step Content */}
          <div className="grid lg:grid-cols-2 gap-6 lg:gap-12 items-start">
            {/* Content */}
            <div className="space-y-6 sm:space-y-8">
              <div>
                <h3 className="text-xl sm:text-2xl font-bold text-primary mb-3 sm:mb-4 color-brand-dark">
                  {processSteps?.[activeStep]?.title}
                </h3>
                <p className="text-muted-foreground leading-relaxed text-sm sm:text-base mb-4 sm:mb-6">
                  {processSteps?.[activeStep]?.description}
                </p>
              </div>

              {/* Challenge & Solution */}
              <div className="space-y-4 sm:space-y-6">
                <div className="p-4 sm:p-6 bg-card border-2 border-concrete">
                  <div className="flex items-center space-x-3 mb-3">
                    <Icon name="AlertTriangle" size={20} className="text-warning" />
                    <h4 className="font-bold text-primary color-brand-dark">Challenge</h4>
                  </div>
                  <p className="text-muted-foreground">
                    {processSteps?.[activeStep]?.challenge}
                  </p>
                </div>

                <div className="p-6 bg-card border-2 border-concrete">
                  <div className="flex items-center space-x-3 mb-3">
                    <Icon name="CheckCircle" size={20} className="text-success" />
                    <h4 className="font-bold text-primary color-brand-dark">Solution</h4>
                  </div>
                  <p className="text-muted-foreground">
                    {processSteps?.[activeStep]?.solution}
                  </p>
                </div>
              </div>

              {/* Tools & Methods */}
              <div>
                <h4 className="font-bold text-primary mb-4 color-brand-dark">Tools & Methods Used</h4>
                <div className="flex flex-wrap gap-2">
                  {processSteps?.[activeStep]?.tools?.map((tool, index) => (
                    <span
                      key={index}
                      className="px-3 py-1 bg-concrete-light text-sm font-medium text-primary border border-concrete"
                    >
                      {tool}
                    </span>
                  ))}
                </div>
              </div>

              {/* Key Insights */}
              <div className="p-6 bg-[#f4eedd] border-2 border-accent">
                <h4 className="font-bold text-primary mb-3 flex items-center space-x-2">
                  <Icon name="Lightbulb" size={20} />
                  <span>Key Insight</span>
                </h4>
                <p className="text-muted-foreground italic">
                  "{processSteps?.[activeStep]?.insight}"
                </p>
              </div>
            </div>

            {/* Visual Documentation Placeholder */}
            <div className="space-y-6">
              <div className="aspect-video bg-concrete-light border-2 border-concrete flex items-center justify-center">
                <div className="text-center">
                  <Icon name="Image" size={32} className="text-concrete-dark mx-auto mb-2" />
                  <div className="text-concrete-dark text-sm">Process Visualization</div>
                </div>
              </div>

              {/* Metrics for this step */}
              {processSteps?.[activeStep]?.metrics && (
                <div className="grid grid-cols-2 gap-4">
                  {processSteps?.[activeStep]?.metrics?.map((metric, index) => (
                    <div key={index} className="text-center p-4 bg-card border-2 border-concrete">
                      <div className="text-2xl font-black text-accent">{metric?.value}</div>
                      <div className="text-sm font-semibold text-muted-foreground">
                        {metric?.label}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProcessDocumentation;